import { studioRenderRequest, type StudioRenderRequest } from './studioRenderRequest';

/** A FreeCut project as stored in `clip.data.freecutProject` (opaque to autoclipper). */
export type FreecutProject = Record<string, unknown> & { schemaVersion: number };

/** What FreeCut Studio imports when the clip has no saved studio project (or starts over). */
export type StudioImport =
  | { kind: 'import'; request: StudioRenderRequest }
  | { kind: 'unavailable' }
  | { kind: 'unsupported'; composition: string };

const isFreecutProject = (value: unknown): value is FreecutProject =>
  !!value && typeof value === 'object' && typeof (value as { schemaVersion?: unknown }).schemaVersion === 'number';

/** The project the studio saved for this clip, if any. */
export function savedStudioProject(clip: { data?: unknown }): FreecutProject | null {
  const saved = (clip.data as { freecutProject?: unknown } | null | undefined)?.freecutProject;
  return isFreecutProject(saved) ? saved : null;
}

/**
 * The import of the clip's render data (`myClipRenderData`: the clip as it would export now,
 * exported before or not).
 */
export function studioImport(renderData: unknown, supportedCompositions: readonly string[]): StudioImport {
  const request = studioRenderRequest(renderData);
  if (!request) return { kind: 'unavailable' };
  if (!supportedCompositions.includes(request.composition)) {
    return { kind: 'unsupported', composition: request.composition };
  }
  return { kind: 'import', request };
}

/** `updateClipData` patch saving the studio project (a shallow, atomic merge server-side). */
export function studioSavePatch(project: FreecutProject): { freecutProject: FreecutProject } {
  return { freecutProject: project };
}
